import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../ui/Card';
import StatusBadge from '../ui/StatusBadge';
import LoggerGroup from './LoggerGroup';
import type { SiteWithData } from '../../hooks/useDashboardData';

interface SiteSectionProps {
  siteData: SiteWithData;
}

export default function SiteSection({ siteData }: SiteSectionProps) {
  const { t } = useTranslation();
  const { site, loggers, status } = siteData;
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card className="overflow-hidden">
      {/* En-tête Site (cliquable pour collapse/expand) */}
      <div
        className="flex items-center justify-between cursor-pointer hover:bg-gray-50 -m-4 p-4 transition-colors"
        onClick={toggleExpanded}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
          }
        }}
        aria-expanded={isExpanded}
        aria-label={isExpanded ? t('dashboard.collapseSite') : t('dashboard.expandSite')}
      >
        <div className="flex items-center gap-3">
          {/* Icône chevron */}
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform ${
              isExpanded ? 'rotate-90' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>

          {/* Nom du site */}
          <h2 className="text-xl font-bold text-gray-900">{site.name}</h2>

          {/* Nombre de loggers */}
          <span className="text-sm text-gray-500">
            ({loggers.length} {loggers.length > 1 ? t('site.loggersPlural') : t('site.loggersSingular')})
          </span>
        </div>

        {/* Badge de statut agrégé (masqué si OK) */}
        {status !== 'ok' && <StatusBadge status={status} />}
      </div>

      {/* Contenu collapsible */}
      {isExpanded && (
        <div className="mt-4">
          {loggers.length > 0 ? (
            <div className="space-y-4">
              {loggers.map((loggerData) => (
                <LoggerGroup
                  key={loggerData.logger.id}
                  loggerData={loggerData}
                  siteName={site.name}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">{t('site.noLoggers')}</p>
          )}
        </div>
      )}
    </Card>
  );
}
